/*
 * SPDX-FileCopyrightText: 2023 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import { useApplicationState } from '../../../hooks/common/use-application-state'
import { useTranslatedText } from '../../../hooks/common/use-translated-text'
import { useSaveDarkModePreferenceToLocalStorage } from '../../../hooks/dark-mode/use-save-dark-mode-preference-to-local-storage'
import type { CommonModalProps } from '../../common/modals/common-modal'
import { CommonModal } from '../../common/modals/common-modal'
import { NoteInfoSection } from '../../common/note-info/note-info-section'
import { NoteSettingsProvider } from '../../common/note-settings/note-settings-context'
import { EditorSettingsTabContent } from './editor/editor-settings-tab-content'
import { GlobalSettingsTabContent } from './global/global-settings-tab-content'
import React from 'react'
import { Modal, Tab, Tabs } from 'react-bootstrap'
import { Gear as IconGear } from 'react-bootstrap-icons'

export interface SettingsModalProps extends CommonModalProps {
  showNoteInfo?: boolean
}

/**
 * Shows global and scope specific settings
 *
 * @param show if the modal should be visible
 * @param onHide callback that is executed if the modal should be closed
 * @param showNoteInfo if the info of the opened note should be shown in an additional tab
 */
export const SettingsModal: React.FC<SettingsModalProps> = ({ show, onHide, showNoteInfo = false }) => {
  const globalLabelTitle = useTranslatedText('settings.global.label')
  const editorLabelTitle = useTranslatedText('settings.editor.label')
  const noteInfoLabelTitle = useTranslatedText('editor.noteInfo.title')
  const openNoteAlias = useApplicationState((state) => state.noteDetails?.primaryAlias)
  useSaveDarkModePreferenceToLocalStorage()

  return (
    <CommonModal
      show={show}
      modalSize={'lg'}
      onHide={onHide}
      titleIcon={IconGear}
      titleI18nKey={'settings.title'}
      showCloseButton={true}>
      <Modal.Body>
        <Tabs navbar={false} variant={'tabs'} defaultActiveKey={'global'}>
          <Tab title={globalLabelTitle} eventKey={'global'}>
            <GlobalSettingsTabContent />
          </Tab>
          <Tab title={editorLabelTitle} eventKey={'editor'}>
            <EditorSettingsTabContent />
          </Tab>
          {showNoteInfo && openNoteAlias !== undefined && (
            <Tab title={noteInfoLabelTitle} eventKey={'note-info'} className={'pt-3'}>
              <NoteSettingsProvider noteAlias={openNoteAlias}>
                <NoteInfoSection />
              </NoteSettingsProvider>
            </Tab>
          )}
        </Tabs>
      </Modal.Body>
    </CommonModal>
  )
}
